import React from 'react';
import { ArrowPathIcon, LanguageIcon, TrashIcon } from '@heroicons/react/24/outline';
import DeepLModelPicker from '../../../components/translation/DeepLModelPicker';
import { getReportTranslationLanguageName } from '../../../constants/reportTranslationLanguages';
import { isRunningStatus } from '../../../constants/jobStatus';

/**
 * Language switcher for a generated custom report.
 *
 * The original is always present; every other entry is a DeepL translation with
 * its own status, so a language can be switched to, retried, or removed here.
 */
const CustomReportLanguages = ({
  languages = [],
  missing = [],
  activeLanguage,
  busy = false,
  onSelect,
  onTranslate,
  onDelete,
}) => {
  if (languages.length === 0) return null;

  // The engine only matters when a translation can still be started or retried.
  const canTranslate = missing.length > 0 || languages.some((l) => l.status === 'FAILED');

  return (
    <div className="custom-reports-langs">
      <span className="custom-reports-langs-label">
        <LanguageIcon /> Language
      </span>

      {languages.map((language) => {
        const isBusy = isRunningStatus(language.status);
        const failed = language.status === 'FAILED';
        const name = language.name || getReportTranslationLanguageName(language.code);
        return (
          <span key={language.code} className="custom-reports-lang">
            <button
              type="button"
              className={`custom-reports-lang-btn${activeLanguage === language.code ? ' active' : ''}`}
              onClick={() => onSelect?.(language.code)}
              disabled={isBusy || failed}
              title={
                failed
                  ? language.error_message || 'Translation failed'
                  : language.is_stale
                    ? `${name} — the original was edited after this translation was made`
                    : name
              }
            >
              {isBusy && <ArrowPathIcon className="custom-reports-spin" />}
              {name}
              {language.is_original && ' (original)'}
              {isBusy && ` — ${Math.round(language.progress || 0)}%`}
              {failed && ' — failed'}
              {!isBusy && !failed && language.is_stale && ' — outdated'}
            </button>

            {failed && (
              <button
                type="button"
                className="custom-reports-link"
                onClick={() => onTranslate?.(language.code)}
                disabled={busy}
              >
                Retry
              </button>
            )}
            {!language.is_original && !isBusy && (
              <button
                type="button"
                className="custom-reports-lang-remove"
                onClick={() => onDelete?.(language.code)}
                title={`Remove ${name} translation`}
                disabled={busy}
              >
                <TrashIcon />
              </button>
            )}
          </span>
        );
      })}

      {missing.map((language) => (
        <button
          key={language.code}
          type="button"
          className="custom-reports-link"
          onClick={() => onTranslate?.(language.code)}
          disabled={busy}
        >
          Translate to {language.name}
        </button>
      ))}

      {canTranslate && (
        <div className="custom-reports-langs-engine">
          <DeepLModelPicker disabled={busy} />
        </div>
      )}
    </div>
  );
};

export default CustomReportLanguages;
